import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomisation } from "../../context/CustomisationContext";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "../../context/TranslationContext";
import { useAppBootstrap } from "../../hooks/useAppBootstrap";
import { getUserPool, login } from "../../services/api/apiClient";
import { setAxiosUserEmail } from "../../services/api/axiosInstance";
import { loginUserParams } from "../../services/api/types/auth/login";
import {
  LoginFlow,
  UserEmailQueryParams,
} from "../../services/api/types/auth/userPool";
import { getShadeColour, isValidEmail } from "../../utils/helpers";
import { FooterLogo } from "../components/FooterLogo";

interface LoginProps {}

export const Login: React.FC<LoginProps> = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { customisations } = useCustomisation();
  const { translations } = useTranslation();
  const runBootstrap = useAppBootstrap();

  const loginTranslations = translations.translations.login;
  const {
    LOGIN: {
      textColorsSettings: loginTextColors,
      backgroundSettings: loginBgSettings,
      logoSettings: loginLogoSettings,
      inputFieldsSettings,
      buttonsSettings: loginbuttonssettings,
    },
    GPC: {
      textColorsSettings: gpcTextColors,
      backgroundSettings: gpcBgSettings,
      logoSettings: gpcLogoSettings,
      buttonsSettings: gpcbuttonssettings,
    },
  } = customisations.customisations;
  const secondaryTextColor =
    loginTextColors?.loginSecondaryTextColor ??
    gpcTextColors?.gpcSecondaryTextColor;
  const primaryBgColor =
    loginBgSettings?.loginPrimaryBgColor ?? gpcBgSettings?.gpcPrimaryBgColor;
  const secondaryBgColor =
    loginBgSettings?.loginSecondaryBgColor ??
    gpcBgSettings?.gpcSecondaryBgColor;
  const logo =
    loginLogoSettings?.loginLandscapeLogo ?? gpcLogoSettings?.gpcLandscapeLogo;
  const secondaryButtonBg =
    loginbuttonssettings?.loginBtnSecondaryBgColor ??
    gpcbuttonssettings?.gpcBtnPrimaryBgColor;
  const secondaryButtonText =
    loginbuttonssettings?.loginBtnSecondaryTextColor ??
    gpcbuttonssettings?.gpcBtnSecondaryTextColor;
  const linkColor =
    loginbuttonssettings?.loginLinkColor ?? gpcbuttonssettings?.gpcLinkColor;

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailError("");
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordError("");
    setPassword(event.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      if (!isValidEmail(email)) {
        setEmailError("Enter a valid email");
        return;
      }
      if (password.trim() === "") {
        setPasswordError("Enter a password");
        return;
      }
      const normalizedEmail = email.toLowerCase().trim();
      const emailQueryParams: UserEmailQueryParams = {
        email: normalizedEmail,
        loginFlow: LoginFlow.LOGIN,
        portalUrl: window.location.origin,
      };
      const userPoolResponse = await getUserPool(emailQueryParams);
      setAxiosUserEmail(userPoolResponse.cognitoUser);

      const body: loginUserParams = {
        username: userPoolResponse.cognitoUser,
        password: password,
        inFlow: false,
        poolId: userPoolResponse.poolId,
        clientId: userPoolResponse.clientId,
        portalId: userPoolResponse.portalId,
        userId: userPoolResponse.userId,
      };
      await login(body);

      localStorage.setItem("userName", normalizedEmail);
      localStorage.setItem("rememberMe", JSON.stringify(true));
      await runBootstrap();
      navigate("/home");
    } catch (error: any) {
      // if (error.response?.data === "Account not yet activated.") {
      //   navigate("/link-expired", {
      //     state: { userName: email, reason: "link-expired" },
      //   });
      //   return;
      // }
      showToast(error.response.data, "error");
      console.log("Error: ", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        py: 6,
        bgcolor: primaryBgColor,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Container maxWidth="lg">
          <Card
            sx={{
              maxWidth: 440,
              mx: "auto",
              p: 4,
              borderRadius: 2,
              bgcolor: secondaryBgColor,
              boxShadow:
                "0 1px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)",
            }}
          >
            <Stack spacing={4} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 50,
                  maxWidth: 200,
                }}
              >
                <Box
                  component="img"
                  src={`/${logo}`}
                  alt="logo"
                  sx={{
                    height: 50,
                    maxWidth: 200,
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Stack spacing={1} width="100%" alignItems="center">
                <Typography
                  variant="h5"
                  align="center"
                  color={secondaryTextColor}
                >
                  {loginTranslations.loginHeader?.loginText}
                </Typography>
                <Typography
                  variant="body1"
                  align="center"
                  color={secondaryTextColor}
                >
                  {loginTranslations.loginHeader?.loginDsc}
                </Typography>
              </Stack>

              <Stack spacing={2} width="100%">
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color={secondaryTextColor}>
                    {loginTranslations.loginForm?.email}
                  </Typography>
                  <TextField
                    fullWidth
                    id="email"
                    placeholder="Enter your email address"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !loading) {
                        handleLogin();
                      }
                    }}
                    variant="outlined"
                    size="small"
                    error={Boolean(emailError)}
                    helperText={emailError}
                    slotProps={{
                      input: {
                        sx: {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: inputFieldsSettings?.inputBorderColor,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: inputFieldsSettings?.inputBorderColor,
                          },
                          backgroundColor: inputFieldsSettings?.inputBGColor,
                        },
                        endAdornment: emailError && (
                          <ErrorOutlineIcon color="error" fontSize="small" />
                        ),
                      },
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="subtitle2" color={secondaryTextColor}>
                    {loginTranslations.loginForm?.password}
                  </Typography>
                  <TextField
                    fullWidth
                    id="password"
                    placeholder="••••••••"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !loading) {
                        handleLogin();
                      }
                    }}
                    variant="outlined"
                    size="small"
                    error={Boolean(passwordError)}
                    helperText={passwordError}
                    slotProps={{
                      input: {
                        sx: {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: inputFieldsSettings?.inputBorderColor,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: inputFieldsSettings?.inputBorderColor,
                          },
                          backgroundColor: inputFieldsSettings?.inputBGColor,
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge="end"
                              size="small"
                              sx={{
                                border: "none !important",
                                bgcolor: "transparent !important",
                              }}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Stack>
                <Box>
                  <Link
                    style={{
                      color: linkColor,
                      textDecoration: "none",
                      display: "flex",
                      justifySelf: "flex-end",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = "none";
                    }}
                    to={"/forgot-password"}
                  >
                    {loginTranslations.loginForm?.btnForgotPassword}
                  </Link>
                </Box>
              </Stack>
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <Button
                  fullWidth
                  onClick={handleLogin}
                  sx={{
                    py: 1.5,
                    borderRadius: "12px",
                    backgroundColor: `${secondaryButtonBg} !important`,
                    color: `${secondaryButtonText}`,
                    "&:hover": {
                      bgcolor: `${getShadeColour(secondaryButtonBg!, -8)} !important`,
                      fontWeight: 700,
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {loginTranslations.loginForm?.btnLogin}
                  </Typography>
                </Button>
              )}
            </Stack>
          </Card>
        </Container>
      </Box>
      {customisations.customisations.LOGIN.logoSettings?.loginPoweredBy?.toLowerCase() ===
      "true" ? (
        <FooterLogo />
      ) : null}
    </Box>
  );
};
